/**
 * Creatable location tag combobox for repeatable unit editors (#999).
 * Inline "+ Add …" complements toolbar Location tags management.
 */

import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
} from 'react';
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  Pressable,
  StyleSheet,
  Platform,
  Modal,
  ScrollView,
  KeyboardAvoidingView,
  useWindowDimensions,
  type StyleProp,
  type ViewStyle,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { useOptionalLocationTagsDraft } from '../../contexts/LocationTagsDraftContext';
import { useRequestId } from '../../contexts/RequestStageContext';
import { useCreateLocationTag } from '../../hooks/mutations/useCreateLocationTag';
import { useLocationTags } from '../../hooks/queries/useLocationTags';
import { useLocationTagMutationAuthority } from '../../hooks/useLocationTagMutationAuthority';
import { colors } from '../../theme/colors';
import { elevation } from '../../theme/elevation';
import { spacing } from '../../theme/spacing';
import { fontSize, fontWeight, lineHeight } from '../../theme/typography';
import { compareLocationTagCreationOrder } from '../../utils/groupUnitsByLocationThenStatus';
import {
  isDraftLocationTagRequestId,
  isOfflineClientRequestId,
  isPersistedServerRequestId,
} from '../../utils/locationTagRequestId';
import { showToast } from '../../utils/toast';
import {
  resolveIsViewOnlyPresentation,
  ViewOnlyFieldValue,
} from '../forms/viewOnlyFieldUtils';
import { ChevronDown, ChevronUp, MapPin, X } from '../icons';

import type { LocationTag } from '../../types/locationTag';

export interface LocationTagPickerProps {
  requestId?: string | null;
  value?: number | null;
  onChange: (locationTagId: number | null) => void;
  disabled?: boolean;
  readOnlyMode?: boolean;
  requestStage?: string;
  testID?: string;
  containerStyle?: StyleProp<ViewStyle>;
}

function sortTags(tags: LocationTag[]): LocationTag[] {
  return [...tags].sort((a, b) => compareLocationTagCreationOrder(a.id, b.id));
}

function filterTagsByQuery(tags: LocationTag[], query: string): LocationTag[] {
  const normalized = query.trim().toLowerCase();
  if (!normalized) {
    return tags;
  }
  return tags.filter(tag => tag.name.toLowerCase().includes(normalized));
}

function getCreatePortal():
  | ((ui: React.ReactNode, container: Element) => React.ReactNode)
  | null {
  if (Platform.OS !== 'web') {
    return null;
  }
  try {
    return require('react-dom').createPortal as (
      ui: React.ReactNode,
      container: Element,
    ) => React.ReactNode;
  } catch {
    return null;
  }
}

export const LocationTagPicker: React.FC<LocationTagPickerProps> = ({
  requestId,
  value,
  onChange,
  disabled = false,
  readOnlyMode = false,
  requestStage,
  testID = 'pd-location-tag-picker',
  containerStyle,
}) => {
  const draftContext = useOptionalLocationTagsDraft();
  const contextRequestId = useRequestId();
  const resolvedRequestId = requestId || contextRequestId;
  const createTag = useCreateLocationTag();
  const insets = useSafeAreaInsets();
  const { height: windowHeight } = useWindowDimensions();
  const anchorRef = useRef<View>(null);
  const launcherInputRef = useRef<TextInput>(null);
  const sheetInputRef = useRef<TextInput>(null);
  const sheetMaxHeightRef = useRef(Math.round(windowHeight * 0.7));
  const nativeDismissedRef = useRef(false);
  const [query, setQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [menuPosition, setMenuPosition] = useState({
    top: 0,
    left: 0,
    width: 0,
  });
  const [isCreating, setIsCreating] = useState(false);
  const [isActivelyFiltering, setIsActivelyFiltering] = useState(false);
  const suppressNativeFocusRestoration = useCallback(() => {
    if (Platform.OS !== 'web') {
      // Ignore focus restored by modal dismissal until the next deliberate tap.
      nativeDismissedRef.current = true;
    }
  }, []);

  const closeSheet = useCallback(() => {
    suppressNativeFocusRestoration();
    setIsOpen(false);
  }, [suppressNativeFocusRestoration]);

  const isDraftMode =
    draftContext != null && isDraftLocationTagRequestId(resolvedRequestId);
  const canUseServerTags =
    isPersistedServerRequestId(resolvedRequestId) ||
    isOfflineClientRequestId(resolvedRequestId);
  const effectiveRequestId = resolvedRequestId ?? '';

  const { data: serverTags = [], isLoading: isServerLoading } = useLocationTags(
    effectiveRequestId,
    canUseServerTags && !isDraftMode,
  );

  const tags = useMemo(() => {
    if (isDraftMode) {
      return draftContext!.draftTags.map(draft => ({
        id: draft.clientId,
        requestId: 0,
        name: draft.name,
      }));
    }
    return serverTags;
  }, [draftContext, isDraftMode, serverTags]);

  const sortedTags = useMemo(() => sortTags(tags), [tags]);
  const isLoading = !isDraftMode && isServerLoading;
  const { canMutate: hasMutationAuthority } = useLocationTagMutationAuthority(
    resolvedRequestId,
    requestStage,
  );

  const selectedName = useMemo(() => {
    if (value == null) {
      return '';
    }
    return sortedTags.find(tag => tag.id === value)?.name ?? '';
  }, [sortedTags, value]);

  useEffect(() => {
    if (!isOpen) {
      setQuery(selectedName);
      setIsActivelyFiltering(false);
    }
  }, [selectedName, isOpen]);

  const menuFilterQuery = isActivelyFiltering ? query : '';

  const filteredTags = useMemo(
    () => filterTagsByQuery(sortedTags, menuFilterQuery),
    [menuFilterQuery, sortedTags],
  );

  const trimmedQuery = isActivelyFiltering ? query.trim() : '';
  const hasExactMatch = sortedTags.some(
    tag => tag.name.toLowerCase() === trimmedQuery.toLowerCase(),
  );
  const canManageTags =
    !disabled &&
    !readOnlyMode &&
    hasMutationAuthority &&
    !isLoading &&
    !isCreating &&
    (isDraftMode || canUseServerTags);
  const canCreateInline =
    canManageTags &&
    isActivelyFiltering &&
    trimmedQuery.length > 0 &&
    !hasExactMatch;

  /** When typing a new name with no partial matches, keep all tags visible for quick pick. */
  const displayTags = useMemo(() => {
    if (filteredTags.length > 0) {
      return filteredTags;
    }
    if (canCreateInline && sortedTags.length > 0) {
      return sortedTags;
    }
    return filteredTags;
  }, [canCreateInline, filteredTags, sortedTags]);

  const showExistingSection = displayTags.length > 0;
  const showAddRow =
    canManageTags && isOpen && (!hasExactMatch || !isActivelyFiltering);
  const showMenu = isOpen && (showExistingSection || showAddRow);

  useEffect(() => {
    if (!showMenu || Platform.OS === 'web') {
      return undefined;
    }
    const focusTimer = setTimeout(() => {
      sheetInputRef.current?.focus();
    }, 50);
    return () => clearTimeout(focusTimer);
  }, [showMenu]);

  const addRowLabel =
    canCreateInline && trimmedQuery.length > 0
      ? `+ Add "${trimmedQuery}"`
      : '+ Add New tags';

  const updateMenuPosition = useCallback(() => {
    if (Platform.OS !== 'web' || typeof window === 'undefined') {
      return;
    }
    const anchor = anchorRef.current as unknown as HTMLElement | null;
    if (!anchor?.getBoundingClientRect) {
      return;
    }
    const rect = anchor.getBoundingClientRect();
    setMenuPosition({
      top: rect.bottom + window.scrollY + spacing.formDropdownMenuOffsetY,
      left: rect.left + window.scrollX,
      width: Math.max(rect.width, spacing.formDropdownMenuMinWidth),
    });
  }, []);

  useEffect(() => {
    if (!isOpen || Platform.OS !== 'web') {
      return undefined;
    }
    updateMenuPosition();
    window.addEventListener('scroll', updateMenuPosition, true);
    window.addEventListener('resize', updateMenuPosition);
    return () => {
      window.removeEventListener('scroll', updateMenuPosition, true);
      window.removeEventListener('resize', updateMenuPosition);
    };
  }, [isOpen, updateMenuPosition]);

  useEffect(() => {
    if (!isOpen || Platform.OS !== 'web' || typeof document === 'undefined') {
      return undefined;
    }
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target;
      if (!(target instanceof Node)) {
        return;
      }
      const portal = document.querySelector(
        `[data-location-tag-picker-portal="${testID}"]`,
      );
      if (portal?.contains(target)) {
        return;
      }
      if (anchorRef.current && !anchorRef.current.contains?.(target)) {
        closeSheet();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [closeSheet, isOpen, testID]);

  const handleSelectTag = useCallback(
    (tag: LocationTag) => {
      if (tag.id == null) {
        return;
      }
      onChange(tag.id);
      setQuery(tag.name);
      closeSheet();
    },
    [closeSheet, onChange],
  );

  const handleClear = useCallback(() => {
    onChange(null);
    setQuery('');
    closeSheet();
  }, [closeSheet, onChange]);

  const handleCreateTag = useCallback(async () => {
    if (!canCreateInline || isCreating) {
      return;
    }

    setIsCreating(true);
    try {
      if (isDraftMode && draftContext) {
        const clientId = draftContext.addDraftTag(trimmedQuery);
        onChange(clientId);
        setQuery(trimmedQuery);
        closeSheet();
        return;
      }

      if (!canUseServerTags) {
        return;
      }

      const created = await createTag.mutateAsync({
        requestId: effectiveRequestId,
        data: { name: trimmedQuery },
      });
      if (created.id != null) {
        onChange(created.id);
        setQuery(created.name);
        closeSheet();
      }
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : 'Failed to create location tag.';
      showToast.error(message);
    } finally {
      setIsCreating(false);
    }
  }, [
    canCreateInline,
    canUseServerTags,
    closeSheet,
    createTag,
    draftContext,
    effectiveRequestId,
    isCreating,
    isDraftMode,
    onChange,
    trimmedQuery,
  ]);

  const placeholder = isLoading
    ? 'Loading tags…'
    : 'Type to search or add a location tag';

  const openMenu = useCallback(() => {
    sheetMaxHeightRef.current = Math.round(windowHeight * 0.7);
    setIsActivelyFiltering(false);
    setIsOpen(true);
    updateMenuPosition();
  }, [updateMenuPosition, windowHeight]);

  const existingTagsContent = showExistingSection ? (
    <>
      <Text style={styles.sectionHeader}>EXISTING TAGS</Text>
      {displayTags.map(tag => (
        <Pressable
          key={tag.id ?? tag.name}
          testID={`${testID}-option-${tag.name}`}
          style={({ pressed }) => [
            styles.optionRow,
            pressed && styles.optionRowPressed,
            value === tag.id && styles.optionRowSelected,
          ]}
          onPress={() => handleSelectTag(tag)}
        >
          <MapPin size={16} color={colors.textMuted} />
          <Text style={styles.optionText} numberOfLines={1}>
            {tag.name}
          </Text>
        </Pressable>
      ))}
    </>
  ) : null;

  const addTagContent = showAddRow ? (
    <Pressable
      testID={`${testID}-add-option`}
      style={({ pressed }) => [
        styles.addRow,
        showExistingSection && styles.addRowWithDivider,
        pressed && canCreateInline && styles.optionRowPressed,
        !canCreateInline && styles.addRowDisabled,
      ]}
      onPress={() => {
        if (canCreateInline) {
          void handleCreateTag();
        }
      }}
      disabled={isCreating || !canCreateInline}
    >
      <Text
        style={[
          styles.addRowText,
          !canCreateInline && styles.addRowTextDisabled,
        ]}
      >
        {addRowLabel}
      </Text>
    </Pressable>
  ) : null;

  const menuContent = (
    <View style={styles.menuInner} testID={`${testID}-menu`}>
      {existingTagsContent}
      {addTagContent}
    </View>
  );

  const webPortal =
    Platform.OS === 'web' && showMenu && typeof document !== 'undefined'
      ? (() => {
          const createPortal = getCreatePortal();
          if (!createPortal) {
            return null;
          }
          return createPortal(
            <div
              data-location-tag-picker-portal={testID}
              style={{
                position: 'absolute',
                top: menuPosition.top,
                left: menuPosition.left,
                width: menuPosition.width,
                zIndex: 999999,
                boxSizing: 'border-box',
                backgroundColor: colors.backgroundForm,
                borderWidth: 1,
                borderStyle: 'solid',
                borderColor: colors.inputBorderNeutral,
                borderRadius: spacing.formDropdownBorderRadius,
                paddingTop: spacing.formDropdownListInsetVertical,
                paddingBottom: spacing.formDropdownListInsetVertical,
                paddingLeft: spacing.formDropdownOptionPaddingHorizontal,
                paddingRight: spacing.formDropdownOptionPaddingHorizontal,
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
                ...(elevation.dropdownPanel as CSSProperties),
              }}
            >
              <div
                style={{
                  maxHeight: spacing.formDropdownListMaxHeight,
                  overflowY: 'auto',
                  overflowX: 'hidden',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: spacing.formDropdownOptionRowGap,
                  boxSizing: 'border-box',
                }}
              >
                {menuContent}
              </div>
            </div>,
            document.body,
          );
        })()
      : null;

  const isViewOnlyPresentation = resolveIsViewOnlyPresentation({
    readOnlyMode,
    disabled,
  });
  const isInteractionDisabled =
    disabled ||
    readOnlyMode ||
    !hasMutationAuthority ||
    isLoading ||
    isCreating;
  if (isViewOnlyPresentation) {
    return (
      <ViewOnlyFieldValue
        label="Location Tag"
        value={selectedName}
        testID={testID}
        valueTestID={`${testID}-value`}
        containerStyle={containerStyle}
      />
    );
  }

  return (
    <View style={[styles.field, containerStyle]} testID={testID}>
      <Text style={styles.label}>Location Tag</Text>
      <View
        ref={anchorRef}
        style={[
          styles.controlShell,
          isOpen && styles.controlShellOpen,
          isInteractionDisabled && styles.controlShellDisabled,
        ]}
      >
        <TextInput
          ref={launcherInputRef}
          testID={`${testID}-input`}
          value={query}
          onChangeText={text => {
            setQuery(text);
            setIsActivelyFiltering(true);
            setIsOpen(true);
            updateMenuPosition();
          }}
          onFocus={() => {
            if (Platform.OS !== 'web') {
              // The launcher is display-only on native. Keeping it focused while
              // the modal search field takes focus can make Android alternate
              // focus between the two modal windows.
              launcherInputRef.current?.blur();
            }
            if (Platform.OS === 'web' || !nativeDismissedRef.current) {
              openMenu();
            }
          }}
          onPressIn={() => {
            if (Platform.OS !== 'web' && !isInteractionDisabled) {
              nativeDismissedRef.current = false;
              openMenu();
            }
          }}
          showSoftInputOnFocus={Platform.OS === 'web'}
          placeholder={placeholder}
          placeholderTextColor={colors.inputPlaceholder}
          editable={!isInteractionDisabled}
          style={styles.input}
          accessibilityLabel="Location tag"
          accessibilityHint="Search existing tags or add a new location tag"
          {...Platform.select({
            web: { autoComplete: 'off' as const, spellCheck: false },
          })}
        />
        {!isInteractionDisabled && selectedName ? (
          <TouchableOpacity
            testID={`${testID}-clear`}
            onPress={handleClear}
            style={styles.iconButton}
            accessibilityRole="button"
            accessibilityLabel="Clear location tag"
          >
            <X size={14} color={colors.textMuted} />
          </TouchableOpacity>
        ) : null}
        <TouchableOpacity
          testID={`${testID}-toggle`}
          onPress={() => {
            if (isInteractionDisabled) {
              return;
            }
            if (isOpen) {
              closeSheet();
              return;
            }
            openMenu();
          }}
          disabled={isInteractionDisabled}
          style={styles.iconButton}
          accessibilityRole="button"
          accessibilityLabel="Toggle location tag list"
        >
          {isOpen ? (
            <ChevronUp
              size={spacing.formDropdownChevronSize}
              color={colors.textSecondary}
            />
          ) : (
            <ChevronDown
              size={spacing.formDropdownChevronSize}
              color={colors.textSecondary}
            />
          )}
        </TouchableOpacity>
      </View>

      {webPortal}

      {Platform.OS !== 'web' ? (
        <Modal
          visible={showMenu}
          transparent
          animationType="slide"
          onRequestClose={closeSheet}
        >
          <View style={styles.sheetRoot} testID={`${testID}-sheet-root`}>
            <Pressable
              style={styles.modalBackdrop}
              onPress={closeSheet}
              accessibilityRole="button"
              accessibilityLabel="Dismiss location tag list"
            />
            <KeyboardAvoidingView
              behavior={Platform.OS === 'ios' ? 'padding' : undefined}
              pointerEvents="box-none"
              style={[
                styles.sheetKeyboardAvoid,
                Platform.OS === 'android' && styles.sheetKeyboardAvoidAndroid,
              ]}
            >
              <View
                style={[
                  styles.sheet,
                  {
                    // Android changes windowHeight while its keyboard animates.
                    // Keep the opening height stable to avoid a resize loop.
                    maxHeight: sheetMaxHeightRef.current,
                    paddingBottom: Math.max(insets.bottom, spacing.md),
                  },
                ]}
                testID={`${testID}-sheet`}
              >
                <View style={styles.sheetGrabber} accessibilityElementsHidden />
                <View style={styles.sheetHeader}>
                  <Text style={styles.sheetTitle}>Location Tag</Text>
                  <TouchableOpacity
                    onPress={closeSheet}
                    testID={`${testID}-sheet-done`}
                    accessibilityRole="button"
                    accessibilityLabel="Done"
                    hitSlop={8}
                  >
                    <Text style={styles.sheetDone}>Done</Text>
                  </TouchableOpacity>
                </View>
                <View
                  style={[
                    styles.sheetSearchShell,
                    isInteractionDisabled && styles.controlShellDisabled,
                  ]}
                >
                  <TextInput
                    ref={sheetInputRef}
                    testID={`${testID}-sheet-input`}
                    value={query}
                    onChangeText={text => {
                      setQuery(text);
                      setIsActivelyFiltering(true);
                    }}
                    placeholder={placeholder}
                    placeholderTextColor={colors.inputPlaceholder}
                    editable={!isInteractionDisabled}
                    style={styles.sheetInput}
                    accessibilityLabel="Search or add location tag"
                    accessibilityHint="Search existing tags or add a new location tag"
                  />
                  {!isInteractionDisabled && query.length > 0 ? (
                    <TouchableOpacity
                      testID={`${testID}-sheet-clear`}
                      onPress={() => {
                        setQuery('');
                        setIsActivelyFiltering(true);
                      }}
                      style={styles.iconButton}
                      accessibilityRole="button"
                      accessibilityLabel="Clear location tag search"
                    >
                      <X size={14} color={colors.textMuted} />
                    </TouchableOpacity>
                  ) : null}
                </View>
                <ScrollView
                  style={styles.sheetScroll}
                  contentContainerStyle={styles.sheetScrollContent}
                  keyboardShouldPersistTaps="handled"
                  nestedScrollEnabled
                  showsVerticalScrollIndicator
                >
                  <View style={styles.menuInner} testID={`${testID}-menu`}>
                    {existingTagsContent}
                  </View>
                </ScrollView>
                {addTagContent ? (
                  <View style={styles.sheetActionFooter}>{addTagContent}</View>
                ) : null}
              </View>
            </KeyboardAvoidingView>
          </View>
        </Modal>
      ) : null}
    </View>
  );
};

const styles = StyleSheet.create({
  field: {
    width: '100%',
    minWidth: 0,
    alignSelf: 'stretch',
  },
  label: {
    fontSize: fontSize.formLabel,
    lineHeight: lineHeight.formLabel,
    fontWeight: fontWeight.medium,
    color: colors.textPrimary,
    marginBottom: spacing.xs,
  },
  controlShell: {
    flexDirection: 'row',
    alignItems: 'center',
    minHeight: spacing.formControlHeight,
    borderWidth: 1,
    borderColor: colors.inputBorderNeutral,
    borderRadius: spacing.formDropdownBorderRadius,
    backgroundColor: colors.backgroundForm,
    paddingHorizontal: spacing.formControlPaddingHorizontal,
    gap: spacing.xs,
    ...Platform.select({
      web: {
        boxSizing: 'border-box' as const,
        ':focus-within': {
          borderWidth: 2,
          borderColor: colors.inputBorderInteractive,
        },
      },
      default: {},
    }),
  },
  controlShellOpen: {
    ...Platform.select({
      web: {
        borderWidth: 2,
        borderColor: colors.inputBorderInteractive,
      },
      default: {
        borderColor: colors.inputBorderInteractive,
      },
    }),
  },
  controlShellDisabled: {
    opacity: 0.6,
    backgroundColor: colors.backgroundMuted,
  },
  input: {
    flex: 1,
    minWidth: 0,
    fontSize: fontSize.formControl,
    lineHeight: lineHeight.formControl,
    color: colors.textPrimary,
    paddingVertical: spacing.formControlPaddingVertical,
    ...Platform.select({
      web: { outlineStyle: 'none' } as object,
      default: { paddingVertical: 0 },
    }),
  },
  iconButton: {
    width: 28,
    height: 28,
    alignItems: 'center',
    justifyContent: 'center',
  },
  menuInner: {
    gap: spacing.formDropdownOptionRowGap,
  },
  sectionHeader: {
    fontSize: 11,
    fontWeight: fontWeight.semibold,
    letterSpacing: 0.6,
    color: colors.textMuted,
    textTransform: 'uppercase',
    marginBottom: spacing.xs,
  },
  optionRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    minHeight: spacing.formDropdownOptionMinHeight,
    paddingHorizontal: spacing.formDropdownOptionPaddingHorizontal,
    marginHorizontal: -spacing.formDropdownOptionPaddingHorizontal,
    borderRadius: spacing.formDropdownBorderRadius,
  },
  optionRowPressed: {
    backgroundColor: colors.surfaceHover,
  },
  optionRowSelected: {
    backgroundColor: colors.surfaceSelectSelected,
  },
  optionText: {
    flex: 1,
    fontSize: fontSize.formControl,
    lineHeight: lineHeight.formControl,
    color: colors.textPrimary,
  },
  addRow: {
    minHeight: spacing.formDropdownOptionMinHeight,
    justifyContent: 'center',
    paddingHorizontal: spacing.formDropdownOptionPaddingHorizontal,
    marginHorizontal: -spacing.formDropdownOptionPaddingHorizontal,
    borderRadius: spacing.formDropdownBorderRadius,
  },
  addRowWithDivider: {
    borderTopWidth: 1,
    borderTopColor: colors.borderCard,
    marginTop: spacing.xs,
    paddingTop: spacing.sm,
  },
  addRowText: {
    fontSize: fontSize.formControl,
    lineHeight: lineHeight.formControl,
    color: colors.textPrimary,
  },
  addRowDisabled: {
    opacity: 1,
  },
  addRowTextDisabled: {
    color: colors.textMuted,
  },
  sheetRoot: {
    flex: 1,
    justifyContent: 'flex-end',
  },
  sheetKeyboardAvoid: {
    width: '100%',
  },
  sheetKeyboardAvoidAndroid: {
    flex: 1,
  },
  modalBackdrop: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: 'rgba(0,0,0,0.35)',
  },
  sheet: {
    backgroundColor: colors.backgroundForm,
    borderTopLeftRadius: 16,
    borderTopRightRadius: 16,
    borderWidth: 1,
    borderBottomWidth: 0,
    borderColor: colors.inputBorderNeutral,
    paddingTop: spacing.sm,
    paddingHorizontal: spacing.md,
    ...elevation.dropdownPanel,
  },
  sheetGrabber: {
    alignSelf: 'center',
    width: 36,
    height: 4,
    borderRadius: 2,
    backgroundColor: colors.borderMedium,
    marginBottom: spacing.sm,
  },
  sheetHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: spacing.sm,
    paddingHorizontal: spacing.xs,
  },
  sheetTitle: {
    fontSize: 16,
    fontWeight: fontWeight.semibold,
    color: colors.textPrimary,
  },
  sheetDone: {
    fontSize: 16,
    fontWeight: fontWeight.semibold,
    color: colors.primary,
  },
  sheetSearchShell: {
    flexDirection: 'row',
    alignItems: 'center',
    minHeight: spacing.formControlHeight,
    borderWidth: 1,
    borderColor: colors.inputBorderNeutral,
    borderRadius: spacing.formDropdownBorderRadius,
    backgroundColor: colors.backgroundForm,
    paddingHorizontal: spacing.formControlPaddingHorizontal,
    marginBottom: spacing.sm,
    gap: spacing.xs,
  },
  sheetInput: {
    flex: 1,
    minWidth: 0,
    fontSize: fontSize.formControl,
    lineHeight: lineHeight.formControl,
    color: colors.textPrimary,
    paddingVertical: 0,
  },
  sheetScroll: {
    ...Platform.select({
      android: { flex: 1, minHeight: 0 },
      default: { flexGrow: 0 },
    }),
  },
  sheetScrollContent: {
    paddingBottom: spacing.sm,
    paddingHorizontal: spacing.formDropdownOptionPaddingHorizontal,
  },
  sheetActionFooter: {
    paddingHorizontal: spacing.formDropdownOptionPaddingHorizontal,
  },
});
